import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Product } from '../../../../../../../../../models/interface/product/product';
import { Cfs } from '../../../../../../../../../models/interface/product/cfs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { iif, of, Subscription } from 'rxjs';
import { CtoResponse, FtthService, QueryCtoResponse } from '../../../../../../../../../services/api/ftth/ftth.service';
import { catchError, map, pluck } from 'rxjs/operators';
import { LoaderService } from '../../../../../../../../../core/header-menu/loader/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { ProductDepthLevelCfsActionsComponent } from '../product-depth-level-cfs-actions.component';
import {CharValue} from 'src/app/models/interface/product/charValue';

@Component({
  selector: 'app-change-cto',
  templateUrl: './change-cto.component.html',
  styleUrls: ['./change-cto.component.scss']
})
export class ChangeCtoComponent implements OnInit, OnDestroy {

  changeCtoGroup: FormGroup;

  ctos: CtoResponse[] = [];
  displayedColumns: string[] = ['codigoCto', 'codigoSp1', 'codigoSp2', 'estadoPuerto', 'puertoCto', 'puertoSp2','ubicacionCto', 'actions'];

  loading: boolean;

  private item: Product;
  private territoryUser: string;
  private splitterId: string;
  private splitterLocation: string;
  private ctoCode: string;
  private subscriptions = new Subscription();

  constructor(
    private ftthService: FtthService,
    private loaderService: LoaderService,
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ProductDepthLevelCfsActionsComponent>,
    @Inject(MAT_DIALOG_DATA) private data: {
      item: Product;
      serviceId: number;
      type: string;
      ino: string;
    },
  ) {
  }

  ngOnInit() {
    this.item = this.data.item;

    if (this.data.type !== 'cfs') {
      throw new Error('It must be a CFS');
    }

    this.subscriptions.add(this.loaderService.loading$.subscribe(loading => this.loading = loading));

    this.initForm();
    this.initFormValues();
  }

  private initForm() {
    this.changeCtoGroup = this.formBuilder.group({
      ctoCode: this.formBuilder.control({ value: '', disabled: false }, Validators.required),
      serviceId: this.formBuilder.control({ value: '', disabled: true }, Validators.required),
      changeReason: this.formBuilder.control('', [Validators.required, Validators.minLength(3)]),
    });
  }

  private initFormValues() {
    const serviceId = this.data.serviceId;
    const serviceInfo: Cfs = this.item.cfses.find(service => service.id === serviceId);

    this.territoryUser = this.item.productCharValues.find(productCharValue => productCharValue.specId === 'pc_ftth_territory_user').value;

    for (const cfsCharValue of serviceInfo.cfsCharValues) {
      switch (cfsCharValue.specId) {
        case ('sc_network_cto_code'):
          this.ctoCodeControl.setValue(cfsCharValue.value);
          this.ctoCode = cfsCharValue.value;
          break;
        case ('sc_network_service_id'):
          this.serviceIdControl.setValue(cfsCharValue.value);
          break;
        case ('sc_network_splitter_id'):
          this.splitterId = cfsCharValue.value;
          break;
        case ('sc_network_splitter_location'):
          this.splitterLocation = cfsCharValue.value;
          break;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  searchAlternativeCto(both: boolean) {
    this.ctos = [];

    this.subscriptions.add(iif(
      () => both,
      this.ftthService.queryCto(this.territoryUser, this.serviceIdControl.value, this.data.ino, this.ctoCodeControl.value),
      this.ftthService.queryCto(this.territoryUser, this.serviceIdControl.value, this.data.ino),
    )
      .pipe(
        map(
          (queryCtoResponse: QueryCtoResponse) => {
            return queryCtoResponse.listactos.cto;
          },
        ),
        catchError((error) => {
          this.notificationsService.showNotification(this.translateService.instant('QUERY_CTO.error'), NotificationLevel.ERROR);
          return of([]);
        }),
      )
      .subscribe(
        (ctos) => {
          this.ctos = ctos;
          this.ctos.sort(function (a, b) {
            return a.codigocto.toLowerCase() < b.codigocto.toLowerCase() ? -1 : (a.codigocto.toLowerCase() > b.codigocto.toLowerCase()) ? 1 : 0
          });
        },
        (error) => {
          this.notificationsService.showNotification(this.translateService.instant('QUERY_CTO.error'), NotificationLevel.ERROR);
        }
      ));
  }

  changeCto(cto: CtoResponse) {
    if (this.changeCtoGroup.valid && !this.loading && (cto.estadopuerto === 'FREE' || cto.estadopuerto === 'NO CONECTADO')) {
      this.subscriptions.add(this.ftthService
        .changeCto(
          this.territoryUser,
          this.serviceIdControl.value,
          {
            ctoCode: this.ctoCode,
            splitterCode: this.splitterId,
            splitterPort: this.splitterLocation,
          },
          {
            ctoCode: cto.codigocto,
            ctoPort: cto.puertocto,
            splitterCode: cto.codigosp2,
            splitterPort: cto.puertosp2,
          },
          this.changeReasonControl.value,
          this.data.ino
        )
        .subscribe(
          () => {
            this.dialogRef.close(true);
            this.notificationsService.showNotification(this.translateService.instant('CHANGE_CTO.success'), NotificationLevel.SUCCESS);
          },
          error => {
            this.notificationsService.showNotification(this.translateService.instant('CHANGE_CTO.error'), NotificationLevel.ERROR);
          }));
    }
  }

  isFSANProvisionType(): boolean {
    const provisionType = this.getProvisionType();
    return provisionType && provisionType.value === 'FSAN';
  }

  getProvisionType(): CharValue {
      return this.item.cfses[0].cfsCharValues.find(characteristic => characteristic.specId === 'sc_network_provision_type');
    }

  get ctoCodeControl(): FormControl {
    return this.changeCtoGroup.get('ctoCode') as FormControl;
  }

  get serviceIdControl(): FormControl {
    return this.changeCtoGroup.get('serviceId') as FormControl;
  }

  get changeReasonControl(): FormControl {
    return this.changeCtoGroup.get('changeReason') as FormControl;
  }
}
